import { playArcadeSound } from '../../../shared/audio/audio';
import {
  RANGER_WEAPONS,
  calculateVectorActionPosition,
  getFeedbackIntensity,
} from '../rangerLogic';

export function canPlayFullMotionEffects(scene) {
  return !scene?.reducedMotion;
}

export function playRangerSound(sound) {
  try {
    playArcadeSound(sound);
  } catch (_error) {
    // Audio can be locked or unavailable in tests and first-load browser states.
  }
}

export function createCameraBump(scene, eventType) {
  const intensity = getFeedbackIntensity(eventType, {
    reducedMotion: scene?.reducedMotion,
  });

  if (intensity.shakeMs <= 0 || !scene?.cameras?.main?.shake) {
    return;
  }

  scene.cameras.main.shake(intensity.shakeMs, intensity.shakeAmount);
}

function fadeAndDestroy(scene, target, config) {
  if (!canPlayFullMotionEffects(scene)) {
    scene.time.delayedCall(config.duration ?? 120, () => target.destroy());
    return;
  }

  scene.tweens.add({
    ...config,
    targets: target,
    onComplete: () => target.destroy(),
  });
}

export function showToast(scene, message) {
  scene.toastText?.destroy();
  scene.toastText = scene.add.text(scene.level.width / 2, scene.level.height - 44, message, {
    fontFamily: 'monospace',
    fontSize: '13px',
    color: '#fff3bf',
    stroke: '#322510',
    strokeThickness: 4,
  }).setOrigin(0.5).setDepth(20);
  scene.time.delayedCall(1100, () => {
    scene.toastText?.destroy();
    scene.toastText = null;
  });
}

export function createScorePopup(scene, x, y, message, color = '#fff2ad') {
  const popup = scene.add.text(x, y, message, {
    fontFamily: 'monospace',
    fontSize: '12px',
    color,
    fontStyle: 'bold',
    stroke: '#2a2015',
    strokeThickness: 4,
  }).setOrigin(0.5).setDepth(24);

  fadeAndDestroy(scene, popup, {
    alpha: 0,
    y: y - (canPlayFullMotionEffects(scene) ? 24 : 10),
    duration: 520,
    ease: 'Quad.easeOut',
  });
}

export function createMovementDust(scene) {
  if (!canPlayFullMotionEffects(scene) || scene.time.now - (scene.lastFootstepEffectTime ?? 0) < 170) {
    return;
  }

  scene.lastFootstepEffectTime = scene.time.now;
  const dust = scene.add.ellipse(
    scene.player.x,
    scene.player.y + 20,
    18,
    7,
    0xf1d89b,
    0.24
  ).setDepth(3);

  fadeAndDestroy(scene, dust, {
    alpha: 0,
    scaleX: 1.6,
    scaleY: 1.25,
    duration: 240,
    ease: 'Quad.easeOut',
  });
}

export function createMuzzleFlash(scene, weaponType, aimVector) {
  const position = calculateVectorActionPosition(
    { x: scene.player.x, y: scene.player.y },
    aimVector,
    weaponType === 'grenade' ? 21 : 26
  );
  const color = weaponType === 'grenade' ? 0xffd36b : 0xfff0a8;
  const flash = scene.add.circle(position.x, position.y, weaponType === 'grenade' ? 8 : 5, color, 0.9)
    .setStrokeStyle(1, 0x5b3516, 0.52)
    .setDepth(12);

  fadeAndDestroy(scene, flash, {
    alpha: 0,
    scale: weaponType === 'grenade' ? 1.9 : 1.55,
    duration: 120,
  });

  if (weaponType === 'rifle') {
    const trail = scene.add.rectangle(position.x, position.y, 34, 2, 0xfff2ba, 0.52)
      .setRotation(Math.atan2(aimVector.y, aimVector.x))
      .setDepth(10);

    fadeAndDestroy(scene, trail, {
      alpha: 0,
      scaleX: 1.55,
      duration: 90,
    });
  }
}

export function createImpactEffect(scene, x, y, color = 0xf6d47b, radius = 14) {
  const spark = scene.add.circle(x, y, 5, color, 0.84).setDepth(14);

  fadeAndDestroy(scene, spark, {
    alpha: 0,
    radius,
    duration: 180,
    ease: 'Quad.easeOut',
  });
}

export function createMeleeEffect(scene, aimVector) {
  const sweepReach = RANGER_WEAPONS.melee.range;
  const sweepRadius = Math.max(18, sweepReach * 0.45);
  const position = calculateVectorActionPosition(
    { x: scene.player.x, y: scene.player.y },
    aimVector,
    sweepReach - sweepRadius
  );
  const slash = scene.add.arc(position.x, position.y, sweepRadius, -35, 35, false, 0xffefad, 0.72)
    .setStrokeStyle(5, 0xffefad, 0.82)
    .setRotation(Math.atan2(aimVector.y, aimVector.x))
    .setDepth(13);

  fadeAndDestroy(scene, slash, {
    alpha: 0,
    scale: 1.65,
    duration: 150,
    ease: 'Quad.easeOut',
  });
}

export function createExplosionEffect(scene, x, y, radius = 48) {
  const blast = scene.add.circle(x, y, 10, 0xffc86b, 0.72)
    .setStrokeStyle(3, 0xfff0a8, 0.65)
    .setDepth(13);
  const smoke = scene.add.circle(x, y, 7, 0x5f5a4a, 0.45).setDepth(12);

  fadeAndDestroy(scene, blast, {
    alpha: 0,
    radius,
    duration: 260,
    ease: 'Quad.easeOut',
  });
  fadeAndDestroy(scene, smoke, {
    alpha: 0,
    radius: radius * 0.78,
    duration: 380,
    ease: 'Quad.easeOut',
  });
}

export function createDamageFlash(scene, target, color = 0xfff1b8) {
  if (!target?.scene) {
    return;
  }

  const flash = scene.add.circle(target.x, target.y, 20, color, 0.34)
    .setDepth(18);

  fadeAndDestroy(scene, flash, {
    alpha: 0,
    scale: 1.8,
    duration: 180,
    ease: 'Quad.easeOut',
  });
}

export function createDefeatDebris(scene, x, y) {
  const offsets = [
    { x: -12, y: -8 },
    { x: 10, y: -5 },
    { x: -5, y: 10 },
    { x: 13, y: 8 },
  ];

  offsets.forEach((offset, index) => {
    const shard = scene.add.rectangle(x, y, 5, 4, index % 2 ? 0x5d4934 : 0xd8b66c, 0.92)
      .setRotation(index * 0.7)
      .setDepth(15);

    fadeAndDestroy(scene, shard, {
      alpha: 0,
      x: x + offset.x,
      y: y + offset.y,
      duration: canPlayFullMotionEffects(scene) ? 260 : 120,
      ease: 'Quad.easeOut',
    });
  });
}

export function createLastKnownMarker(scene, position) {
  if (!position) {
    return;
  }

  const marker = scene.add.circle(position.x, position.y, 13, 0xffd06b, 0.13)
    .setStrokeStyle(2, 0xffd06b, 0.48)
    .setDepth(4);

  fadeAndDestroy(scene, marker, {
    alpha: 0,
    scale: canPlayFullMotionEffects(scene) ? 1.7 : 1.15,
    duration: 620,
    ease: 'Quad.easeOut',
  });
}

export function createObjectivePulse(scene, target, color = 0xfff0a8) {
  const pulse = scene.add.circle(target.x, target.y, 16, color, 0.2)
    .setStrokeStyle(2, color, 0.55)
    .setDepth(12);

  fadeAndDestroy(scene, pulse, {
    alpha: 0,
    scale: canPlayFullMotionEffects(scene) ? 2.1 : 1.25,
    duration: 360,
    ease: 'Quad.easeOut',
  });
}

export function createMissionCompleteEffect(scene) {
  createCameraBump(scene, 'missionComplete');
  playRangerSound('levelClear');

  const ring = scene.add.circle(
    scene.level.width / 2,
    scene.level.height / 2 + 44,
    20,
    0xf5f8ff,
    0.16
  )
    .setStrokeStyle(3, 0xfff2ad, 0.66)
    .setDepth(21);

  fadeAndDestroy(scene, ring, {
    alpha: 0,
    scale: canPlayFullMotionEffects(scene) ? 3 : 1.4,
    duration: 620,
    ease: 'Quad.easeOut',
  });
}
